import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useCallback, useState } from 'react';
import { useGameStore } from '../stores/gameStore';
import { usePartySocket } from '../hooks/usePartySocket';
import { sendMessage, getSocket } from '../services/partyService';
import usePiecePlacement from '../hooks/usePiecePlacement';
import PlayButton from '../components/PlayButton';
import WaitingModal from '../components/WaitingModal';
import GameStartCountdown from '../components/GameStartCountdown';
import PhaseIndicator from '../components/PhaseIndicator';
import TurnIndicator from '../components/TurnIndicator';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import ReadyStatusIndicator from '../components/ReadyStatusIndicator';
import PlayerGrid from '../components/PlayerGrid';
import Grid from '../components/Grid';
import PiecePalette from '../components/PiecePalette';
import PlacementControls from '../components/PlacementControls';
import VictoryModal from '../components/VictoryModal';
import HitMarker from '../components/HitMarker';
import MissMarker from '../components/MissMarker';
import PrizeEntry from '../components/PrizeEntry';
import PrizesDisplay from '../components/PrizesDisplay';
import PieceDestroyedPopup from '../components/PieceDestroyedPopup';
import BonusPieceCapturedPopup from '../components/BonusPieceCapturedPopup';
import DestroyedPieceOverlay from '../components/DestroyedPieceOverlay';
import PiecesLostScoreboard from '../components/PiecesLostScoreboard';
import {
  validateSessionId,
  handleSessionError,
  handleConnectionError,
} from '../utils/errorHandler';

function Game() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  // Get PartyKit host from environment or use default
  const partyHost = import.meta.env.VITE_PARTYKIT_HOST || 'localhost:1999';

  // Local error state
  const [sessionError, setSessionError] = useState(null);
  const [connectionErrorDetails, setConnectionErrorDetails] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);

  // Game store state
  const setSessionId = useGameStore((state) => state.setSessionId);
  const gamePhase = useGameStore((state) => state.gamePhase);
  const myPlacedPieces = useGameStore((state) => state.myPlacedPieces);
  const playerId = useGameStore((state) => state.playerId);
  const player1 = useGameStore((state) => state.player1);
  const player2 = useGameStore((state) => state.player2);
  const currentTurn = useGameStore((state) => state.currentTurn);
  const winner = useGameStore((state) => state.winner);
  const myAttacks = useGameStore((state) => state.myAttacks);
  const opponentAttacks = useGameStore((state) => state.opponentAttacks);
  const opponentDestroyedPieces = useGameStore(
    (state) => state.opponentDestroyedPieces
  );
  const myDestroyedPieces = useGameStore((state) => state.myDestroyedPieces);
  const myPrizes = useGameStore((state) => state.myPrizes);
  const hasCapturedBonusPiece = useGameStore(
    (state) => state.hasCapturedBonusPiece
  );
  const player1HasCapturedBonus = useGameStore(
    (state) => state.player1HasCapturedBonus
  );
  const player2HasCapturedBonus = useGameStore(
    (state) => state.player2HasCapturedBonus
  );
  const setPlayer1 = useGameStore((state) => state.setPlayer1);
  const setPlayer2 = useGameStore((state) => state.setPlayer2);
  const setGamePhase = useGameStore((state) => state.setGamePhase);
  const addMyAttack = useGameStore((state) => state.addMyAttack);
  const hasPrizes = useGameStore((state) => state.hasPrizes);
  const pieceDestroyedPopup = useGameStore(
    (state) => state.pieceDestroyedPopup
  );
  const dismissPieceDestroyedPopup = useGameStore(
    (state) => state.dismissPieceDestroyedPopup
  );
  const bonusCapturedPopup = useGameStore((state) => state.bonusCapturedPopup);
  const dismissBonusCapturedPopup = useGameStore(
    (state) => state.dismissBonusCapturedPopup
  );

  // Set session ID and reset state if session changed - MUST happen before connection
  useEffect(() => {
    if (sessionId) {
      const state = useGameStore.getState();
      const persistedSessionId = state.persistedSessionId;

      // If we're navigating to a different session, reset the game state
      if (persistedSessionId && persistedSessionId !== sessionId) {
        console.log(
          '[Game] Session ID changed from',
          persistedSessionId,
          'to',
          sessionId,
          '- resetting game state'
        );
        console.log('[Game] Current state before reset:', {
          gamePhase: state.gamePhase,
          winner: state.winner,
        });
        state.reset();
      }

      setSessionId(sessionId);
      setSessionReady(true);
    }
  }, [sessionId, setSessionId]);

  // Validate session ID on mount
  useEffect(() => {
    const sessionValidationError = handleSessionError(sessionId);
    if (sessionValidationError) {
      console.error(
        '[Game] Session validation failed:',
        sessionValidationError
      );
      setSessionError(sessionValidationError);
      // Redirect to home for invalid sessions after a delay
      if (!sessionValidationError.recoverable) {
        setTimeout(() => {
          navigate('/');
        }, 5000);
      }
    }
  }, [sessionId, navigate]);

  // Connect to PartyKit room (disable autoConnect, we'll connect manually after session is ready)
  const { isConnected, isConnecting, error, connect } = usePartySocket(
    sessionId,
    partyHost,
    {
      autoConnect: false,
      onError: (errorMessage) => {
        const connError = handleConnectionError(errorMessage);
        setConnectionErrorDetails(connError);
        console.error('[Game] Connection error:', connError);
      },
    }
  );

  // Connect once session is ready and validated
  useEffect(() => {
    if (sessionReady && !sessionError && !isConnected && !isConnecting) {
      console.log('[Game] Session ready, connecting to PartyKit');
      connect();
    }
  }, [sessionReady, sessionError, isConnected, isConnecting, connect]);

  // Expose game store and party service to window for E2E testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.__GAME_STORE__ = useGameStore;
      window.__PARTY_SERVICE__ = { sendMessage };
    }
  }, []);

  // Expose socket when connected (for E2E testing)
  useEffect(() => {
    if (typeof window !== 'undefined' && isConnected) {
      const socket = getSocket();
      if (socket) {
        window.__PARTY_SOCKET__ = socket;
      }
    }
  }, [isConnected]);

  // Piece placement hook
  const {
    placedPieces,
    selectedPiece,
    availablePieces,
    currentRotation,
    isPlacementLocked,
    errorMessage,
    previewPosition,
    isPreviewValid,
    selectPiece,
    rotatePiece: rotatePieceHandler,
    placePiece,
    removePiece,
    lockPlacement,
    unlockPlacement,
    areAllPiecesPlaced,
    setPreviewPosition,
    clearPreview,
  } = usePiecePlacement();

  // Determine if pieces are locked (player is ready)
  const isReady = playerId === player1?.id ? player1?.ready : player2?.ready;

  // Track if we should show countdown (game just started)
  const [showCountdown, setShowCountdown] = useState(false);
  const [prevGamePhase, setPrevGamePhase] = useState(gamePhase);

  // Track pending attack (for UI feedback)
  const [pendingAttackCoordinate, setPendingAttackCoordinate] = useState(null);

  // Track attack error messages
  const [attackError, setAttackError] = useState(null);

  // Lock/unlock placement based on player ready status
  useEffect(() => {
    if (isReady && !isPlacementLocked) {
      lockPlacement();
    } else if (!isReady && isPlacementLocked && gamePhase === 'setup') {
      // Unlock placement if player is no longer ready and we're in setup phase
      // This handles reconnections and state synchronization issues
      unlockPlacement();
    }
  }, [isReady, isPlacementLocked, gamePhase, lockPlacement, unlockPlacement]);

  // Sync placed pieces to game store
  useEffect(() => {
    useGameStore.setState({ myPlacedPieces: placedPieces });
  }, [placedPieces]);

  // Detect game start transition (waiting -> playing)
  useEffect(() => {
    if (prevGamePhase === 'waiting' && gamePhase === 'playing') {
      setShowCountdown(true);
    }
    setPrevGamePhase(gamePhase);
  }, [gamePhase, prevGamePhase]);

  // Handler for when player clicks Play/Ready button
  const handlePlayerReady = useCallback(async () => {
    try {
      // First, send all placed pieces to the server
      console.log('[Game] Sending all placed pieces to server');
      for (const piece of placedPieces) {
        // Convert client piece format to server format
        const { clientPieceToServerPiece } =
          await import('../utils/coordinateConversion');
        const serverPiece = clientPieceToServerPiece(piece);

        // Send PLACE_PIECE message
        sendMessage({
          type: 'PLACE_PIECE',
          piece: serverPiece,
        });
      }

      // Optimistic update: immediately set player ready status
      // This provides instant UI feedback while waiting for server confirmation
      if (player1?.id === playerId) {
        setPlayer1({ ...player1, ready: true });
        setGamePhase('waiting');
      } else if (player2?.id === playerId) {
        setPlayer2({ ...player2, ready: true });
        setGamePhase('waiting');
      }

      // Send SET_READY message to server
      sendMessage({
        type: 'SET_READY',
      });
      console.log('[Game] Sent all pieces and SET_READY message');
    } catch (error) {
      console.error(
        '[Game] Failed to send pieces or SET_READY message:',
        error
      );

      // Rollback optimistic update on error
      if (player1?.id === playerId) {
        setPlayer1({ ...player1, ready: false });
        setGamePhase('setup');
      } else if (player2?.id === playerId) {
        setPlayer2({ ...player2, ready: false });
        setGamePhase('setup');
      }
    }
  }, [
    playerId,
    player1,
    player2,
    setPlayer1,
    setPlayer2,
    setGamePhase,
    placedPieces,
  ]);

  // Handler for countdown completion
  const handleCountdownComplete = useCallback(() => {
    setShowCountdown(false);
  }, []);

  // Handler for attack on opponent grid
  const handleAttackCell = useCallback(
    (coordinate) => {
      // Clear previous error
      setAttackError(null);

      // Only allow attacks during playing phase and on my turn
      if (gamePhase !== 'playing' || currentTurn !== playerId) {
        return;
      }

      // Don't allow duplicate attacks
      const alreadyAttacked = myAttacks.some(
        (attack) => attack.coordinate.toUpperCase() === coordinate.toUpperCase()
      );
      if (alreadyAttacked) {
        setAttackError(`Cell ${coordinate} has already been attacked`);
        return;
      }

      // Set pending attack for UI feedback
      setPendingAttackCoordinate(coordinate);

      // Send attack message to server
      try {
        sendMessage({
          type: 'ATTACK',
          coordinate,
        });
      } catch (error) {
        console.error('[Game] Failed to send attack:', error);
        setAttackError('Failed to send attack. Please try again.');
        setPendingAttackCoordinate(null);
      }
    },
    [gamePhase, currentTurn, playerId, myAttacks]
  );

  // Clear pending attack when attacks are updated
  useEffect(() => {
    if (
      pendingAttackCoordinate &&
      myAttacks.some(
        (attack) =>
          attack.coordinate.toUpperCase() ===
          pendingAttackCoordinate.toUpperCase()
      )
    ) {
      setPendingAttackCoordinate(null);
    }
  }, [myAttacks, pendingAttackCoordinate]);

  // Convert attack arrays to attackedCells object for Grid components
  const myAttackedCells = myAttacks.reduce((acc, attack) => {
    acc[attack.coordinate] = attack.result; // 'hit' or 'miss'
    return acc;
  }, {});

  const opponentAttackedCells = opponentAttacks.reduce((acc, attack) => {
    acc[attack.coordinate] = attack.result; // 'hit' or 'miss'
    return acc;
  }, {});

  // Handler for victory modal close
  const handleVictoryModalClose = useCallback(() => {
    // Could navigate back to home or reset game
  }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <div className="bg-dark-charcoal border-2 border-neon-purple shadow-glow-purple rounded-lg p-8">
        {/* Session Error Display */}
        {sessionError && (
          <div className="mb-6">
            <ErrorMessage
              visible={true}
              message={sessionError.message}
              type="error"
              title="Session Error"
            />
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600 mb-2">
                {sessionError.action}
              </p>
              {!sessionError.recoverable && (
                <p className="text-xs text-gray-500">
                  Redirecting to home page in a few seconds...
                </p>
              )}
            </div>
          </div>
        )}

        {/* Connection Error Display */}
        {connectionErrorDetails && !sessionError && (
          <div className="mb-6">
            <ErrorMessage
              visible={true}
              message={connectionErrorDetails.message}
              type={connectionErrorDetails.recoverable ? 'warning' : 'error'}
              title="Connection Issue"
            />
            <div className="mt-2 text-center">
              <p className="text-sm text-gray-600">
                {connectionErrorDetails.action}
              </p>
            </div>
          </div>
        )}

        {/* Header */}
        {!sessionError && (
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-neon-cyan text-glow-cyan mb-2">
              Game Session
            </h2>
            <p className="text-sm text-gray-400">Session ID: {sessionId}</p>

            {/* Connection status */}
            {isConnecting && (
              <div className="mt-2 text-sm text-neon-cyan flex items-center gap-2">
                <LoadingSpinner size="small" />
                Connecting to game server...
              </div>
            )}
            {error && !connectionErrorDetails && (
              <div className="mt-2 text-sm text-neon-pink">Error: {error}</div>
            )}
            {isConnected && (
              <div className="mt-2 text-sm text-neon-green">✓ Connected</div>
            )}
          </div>
        )}

        {/* Phase and Turn Indicators */}
        <div className="mb-6 flex gap-4">
          <PhaseIndicator
            phase={gamePhase}
            playerId={playerId}
            winner={winner}
          />
          <TurnIndicator
            currentTurn={currentTurn}
            playerId={playerId}
            myHasCapturedBonus={
              playerId === player1?.id
                ? player1HasCapturedBonus
                : player2HasCapturedBonus
            }
            opponentHasCapturedBonus={
              playerId === player1?.id
                ? player2HasCapturedBonus
                : player1HasCapturedBonus
            }
          />
        </div>

        {/* Ready Status Indicator - only show during setup/waiting phases */}
        <ReadyStatusIndicator
          player1Ready={player1?.ready || false}
          player2Ready={player2?.ready || false}
          playerId={playerId}
          gamePhase={gamePhase}
        />

        {/* Prize Entry UI - Only show during setup and waiting phases */}
        {(gamePhase === 'setup' || gamePhase === 'waiting') && (
          <>
            <PrizeEntry />
            <PrizesDisplay />
          </>
        )}

        {/* Game Area */}
        <div className="bg-dark-purple/50 border-2 border-neon-cyan/50 rounded-lg p-8">
          {/* Piece Placement UI - Only show during setup phase */}
          {gamePhase === 'setup' && (
            <div className="mb-6">
              {/* Piece Palette */}
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-neon-cyan mb-2">
                  Select a piece to place:
                </h3>
                <PiecePalette
                  availablePieces={availablePieces}
                  selectedPiece={selectedPiece}
                  onSelectPiece={selectPiece}
                  isLocked={isPlacementLocked}
                />
              </div>

              {/* Rotation Controls */}
              {selectedPiece && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-neon-cyan mb-2">
                    Rotate selected piece:
                  </h3>
                  <PlacementControls
                    currentRotation={currentRotation}
                    onRotate={rotatePieceHandler}
                    isLocked={isPlacementLocked}
                  />
                </div>
              )}

              {/* Placement Error Message */}
              {errorMessage && (
                <div className="mb-4">
                  <div
                    data-testid="placement-error"
                    className="p-3 bg-neon-pink/20 border border-neon-pink text-neon-pink rounded-lg"
                  >
                    {errorMessage}
                  </div>
                </div>
              )}

              {/* Placement Status */}
              <div className="mb-4 text-center">
                <p className="text-lg font-semibold text-gray-200">
                  Pieces placed: {placedPieces.length}/5
                </p>
                {isPlacementLocked && (
                  <p className="text-sm text-neon-green mt-2 font-semibold shadow-glow-green">
                    🔒 Locked - Waiting for opponent
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Phase 7.9.3.4: Pieces Lost Scoreboard - Show during playing phase */}
          {gamePhase === 'playing' && (
            <div className="mb-4">
              <PiecesLostScoreboard
                myPiecesLost={myDestroyedPieces.length}
                myTotalPieces={myPlacedPieces.length}
                opponentPiecesLost={opponentDestroyedPieces.length}
                opponentTotalPieces={5}
                myHasCapturedBonus={
                  playerId === player1?.id
                    ? player1HasCapturedBonus
                    : player2HasCapturedBonus
                }
                opponentHasCapturedBonus={
                  playerId === player1?.id
                    ? player2HasCapturedBonus
                    : player1HasCapturedBonus
                }
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* My Grid */}
            <div>
              <h3 className="text-lg font-semibold text-neon-pink text-glow-pink mb-2">
                My Grid
              </h3>
              <div data-locked={isReady ? 'true' : 'false'}>
                <PlayerGrid
                  placedPieces={placedPieces}
                  onCellClick={placePiece}
                  selectedPiece={selectedPiece}
                  previewPosition={previewPosition}
                  isPreviewValid={isPreviewValid}
                  onCellHover={(coordinate) => {
                    if (!isPlacementLocked && selectedPiece) {
                      // Convert coordinate to position
                      const COLUMNS = [
                        'A',
                        'B',
                        'C',
                        'D',
                        'E',
                        'F',
                        'G',
                        'H',
                        'I',
                        'J',
                      ];
                      const match = coordinate.match(/^([A-J])(\d+)$/);
                      if (match) {
                        const [, column, row] = match;
                        const x = COLUMNS.indexOf(column);
                        const y = parseInt(row, 10) - 1;
                        setPreviewPosition({ x, y });
                      }
                    }
                  }}
                  onCellLeave={clearPreview}
                  onRemovePiece={removePiece}
                  isLocked={isPlacementLocked}
                  attackedCells={opponentAttackedCells}
                />
              </div>
            </div>

            {/* Opponent Grid */}
            <div>
              <h3 className="text-lg font-semibold text-neon-pink text-glow-pink mb-2">
                Opponent Grid
              </h3>
              <div className="relative">
                <Grid
                  attackedCells={myAttackedCells}
                  onCellClick={handleAttackCell}
                  isClickable={
                    gamePhase === 'playing' &&
                    currentTurn === playerId &&
                    gamePhase !== 'ended'
                  }
                  pendingAttack={pendingAttackCoordinate}
                />
                {/* Phase 7.9.3.3: Show destroyed piece shapes */}
                <DestroyedPieceOverlay
                  destroyedPieces={opponentDestroyedPieces}
                />
              </div>
            </div>
          </div>

          {/* Attack Error Message */}
          {attackError && gamePhase === 'playing' && (
            <div className="mt-4">
              <div className="p-3 bg-neon-pink/20 border border-neon-pink text-neon-pink rounded-lg text-center shadow-glow-pink">
                {attackError}
              </div>
            </div>
          )}

          {/* Play Button */}
          <div className="mt-8 flex justify-center">
            <PlayButton
              onReady={handlePlayerReady}
              disabled={
                !areAllPiecesPlaced() || !hasPrizes() || isPlacementLocked
              }
            />
          </div>
          {/* Help text for ready button */}
          {!isPlacementLocked && (
            <div className="mt-2 text-center text-sm text-gray-400">
              {!areAllPiecesPlaced() && <p>Place all 5 pieces to continue</p>}
              {areAllPiecesPlaced() && !hasPrizes() && (
                <p>Submit at least 1 prize to continue</p>
              )}
              {areAllPiecesPlaced() && hasPrizes() && (
                <p>Click "Ready to Play" when you're ready!</p>
              )}
            </div>
          )}
        </div>

        {/* Waiting Modal - show when player is ready but game hasn't started */}
        <WaitingModal isOpen={isReady && gamePhase === 'waiting'} />

        {/* Game Start Countdown */}
        <GameStartCountdown
          isActive={showCountdown}
          onComplete={handleCountdownComplete}
        />

        {/* Victory Modal */}
        <VictoryModal
          isOpen={gamePhase === 'ended'}
          didIWin={winner === playerId}
          onClose={handleVictoryModalClose}
          myAttacks={myAttacks}
          myPrizes={myPrizes}
          hasCapturedBonusPiece={hasCapturedBonusPiece}
          onSendPrizeSelection={(prize) => {
            sendMessage({
              type: 'PRIZE_SELECTED',
              selectedPrize: prize,
            });
          }}
        />

        {/* Piece Destroyed Popup (Phase 7.9.3.1) */}
        {pieceDestroyedPopup && (
          <PieceDestroyedPopup
            isOpen={true}
            pieceType={pieceDestroyedPopup.pieceType}
            playerWhoLost={pieceDestroyedPopup.playerWhoLost}
            piecesLostCount={pieceDestroyedPopup.piecesLostCount}
            totalPiecesCount={pieceDestroyedPopup.totalPiecesCount}
            onDismiss={dismissPieceDestroyedPopup}
          />
        )}

        {/* Bonus Piece Captured Popup (Phase 7.9.3.2) */}
        {bonusCapturedPopup && (
          <BonusPieceCapturedPopup
            isOpen={true}
            attackerPlayer={bonusCapturedPopup.attackerPlayer}
            onDismiss={dismissBonusCapturedPopup}
          />
        )}

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-neon-cyan hover:text-neon-pink transition-colors"
          >
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}

export default Game;
